import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  CheckCircle2,
  CircleDot,
  Download,
  RotateCcw,
  StickyNote,
  Archive,
  ArchiveRestore,
  FileText,
  Trash2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABEL = { letter: "Standard Package", full_petty: "Premium Package" };

const FILTERS = [
  { id: "all", label: "All" },
  { id: "unpaid", label: "Unpaid" },
  { id: "paid", label: "Paid" },
  { id: "handled", label: "Handled" },
];

const Badge = ({ tone, children, testid }) => {
  const tones = {
    yellow: "bg-[#2563EB]/20 text-[#1D4ED8] border-[#2563EB]",
    green: "bg-emerald-100 text-emerald-800 border-emerald-300",
    gray: "bg-[#F3F4F6] text-[#71717a] border-[#E5E7EB]",
    red: "bg-red-100 text-red-700 border-red-300",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full border ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [cases, setCases] = useState(null);
  const [stats, setStats] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [noteInputs, setNoteInputs] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [filter, setFilter] = useState("all");
  const token = localStorage.getItem("petty_admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [casesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/cases`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setCases(casesRes.data);
      setStats(statsRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("petty_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Could not load cases.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  useEffect(() => {
    if (!token) navigate("/admin/login");
    else load();
  }, [token, navigate, load]);

  const setStatus = async (id, status) => {
    setUpdating(id);
    try {
      await axios.patch(`${API}/admin/cases/${id}`, { status }, { headers });
      toast.success(
        status === "handled" ? "Case marked handled — customer notified." : "Case reopened."
      );
      await load();
    } catch (e) {
      toast.error("Update failed.");
    } finally {
      setUpdating(null);
    }
  };

  const setArchived = async (id, archived) => {
    setUpdating(id);
    try {
      await axios.patch(`${API}/admin/cases/${id}/archive`, { archived }, { headers });
      toast.success(archived ? "Case archived." : "Case restored.");
      await load();
    } catch (e) {
      toast.error("Archive failed.");
    } finally {
      setUpdating(null);
    }
  };

  const deleteCase = async (id, name) => {
    if (
      !window.confirm(
        `Permanently delete the case for ${name}? This cannot be undone.`
      )
    )
      return;
    setUpdating(id);
    try {
      await axios.delete(`${API}/admin/cases/${id}`, { headers });
      toast.success("Case deleted.");
      await load();
    } catch (e) {
      toast.error("Delete failed.");
    } finally {
      setUpdating(null);
    }
  };

  const downloadLetter = async (id) => {
    setUpdating(id);
    try {
      const res = await axios.get(`${API}/admin/cases/${id}/letter.pdf`, {
        headers,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `demand-letter-${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Letter PDF downloaded.");
    } catch (e) {
      toast.error("Could not generate the letter.");
    } finally {
      setUpdating(null);
    }
  };

  const refund = async (id) => {
    if (!window.confirm("Refund this payment in full via Stripe?")) return;
    setUpdating(id);
    try {
      await axios.post(`${API}/admin/cases/${id}/refund`, {}, { headers });
      toast.success("Payment refunded.");
      await load();
    } catch (e) {
      toast.error(
        typeof e.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Refund failed."
      );
    } finally {
      setUpdating(null);
    }
  };

  const addNote = async (id) => {
    const text = (noteInputs[id] || "").trim();
    if (!text) return;
    setUpdating(id);
    try {
      await axios.post(`${API}/admin/cases/${id}/notes`, { text }, { headers });
      setNoteInputs((n) => ({ ...n, [id]: "" }));
      toast.success("Note added.");
      await load();
    } catch (e) {
      toast.error("Could not add note.");
    } finally {
      setUpdating(null);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/export/cases`, {
        headers,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "no-lawyer-lawsuit-cases.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (e) {
      toast.error("Export failed.");
    }
  };

  const logout = () => {
    localStorage.removeItem("petty_admin_token");
    navigate("/admin/login");
  };

  const applyFilter = (list) => {
    if (filter === "unpaid") return list.filter((c) => c.payment_status === "unpaid");
    if (filter === "paid") return list.filter((c) => c.payment_status === "paid");
    if (filter === "handled") return list.filter((c) => c.status === "handled");
    return list;
  };

  const activeCases = applyFilter(cases?.filter((c) => !c.archived) ?? []);
  const archivedCases = cases?.filter((c) => c.archived) ?? [];

  const renderCase = (c, i) => (
    <motion.div
      key={c.id}
      data-testid={`case-row-${c.id.slice(0, 8)}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.4) }}
      className={`bg-white border border-[#E5E7EB] rounded-3xl p-6 ${
        c.status === "handled" || c.archived ? "opacity-70" : ""
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-[220px]">
          <p className="font-display font-semibold text-xl leading-tight">{c.name}</p>
          <p className="text-sm text-[#71717a]">{c.email}</p>
          {c.phone && <p className="text-sm text-[#71717a]">{c.phone}</p>}
          <p className="text-xs text-[#a1a1aa] mt-1.5">
            {new Date(c.created_at).toLocaleString()} · Ref {c.id.slice(0, 8)}
          </p>
          <button
            data-testid={`case-letter-${c.id.slice(0, 8)}`}
            onClick={() => downloadLetter(c.id)}
            disabled={updating === c.id}
            className="mt-3 flex items-center gap-1.5 text-xs font-bold bg-[#2563EB]/20 border border-[#2563EB] text-[#1D4ED8] px-3.5 py-2 rounded-full hover:bg-[#2563EB]/40 transition-colors disabled:opacity-50"
          >
            <FileText size={13} /> Letter PDF
          </button>
        </div>
        <div className="flex-1 min-w-[220px]">
          <p className="text-sm">
            <span className="font-bold">{c.dispute_type}</span>
            <span className="text-[#71717a]">
              {" "}
              · {c.amount} owed by {c.opponent}
            </span>
          </p>
          <p className="text-sm text-[#52525B] mt-1.5 leading-relaxed">{c.description}</p>

          <div className="mt-4 bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-4">
            {(c.notes || []).length > 0 && (
              <ul className="space-y-2 mb-3">
                {c.notes.map((n, idx) => (
                  <li key={idx} className="text-xs text-[#52525B] flex gap-2">
                    <StickyNote size={13} className="text-[#2563EB] mt-0.5 shrink-0" />
                    <span>
                      {n.text}{" "}
                      <span className="text-[#a1a1aa]">
                        · {new Date(n.created_at).toLocaleDateString()}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <input
                data-testid={`note-input-${c.id.slice(0, 8)}`}
                value={noteInputs[c.id] || ""}
                onChange={(e) => setNoteInputs((n) => ({ ...n, [c.id]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && addNote(c.id)}
                placeholder="Add a private note..."
                className="flex-1 bg-white border border-[#E5E7EB] rounded-full px-4 py-2 text-xs focus:outline-none focus:border-[#2563EB]"
              />
              <button
                data-testid={`note-add-${c.id.slice(0, 8)}`}
                onClick={() => addNote(c.id)}
                disabled={updating === c.id}
                className="text-xs font-bold bg-[#111827] text-[#FFFFFF] px-4 py-2 rounded-full hover:bg-[#1F2937] transition-colors disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap gap-2 justify-end">
            <Badge tone="yellow" testid={`case-plan-${c.id.slice(0, 8)}`}>
              {PLAN_LABEL[c.plan] || c.plan}
            </Badge>
            {c.payment_status === "paid" ? (
              <Badge tone="green" testid={`case-payment-${c.id.slice(0, 8)}`}>Paid</Badge>
            ) : c.payment_status === "refunded" ? (
              <Badge tone="red" testid={`case-payment-${c.id.slice(0, 8)}`}>Refunded</Badge>
            ) : (
              <Badge tone="gray" testid={`case-payment-${c.id.slice(0, 8)}`}>Unpaid</Badge>
            )}
            {c.status === "handled" ? (
              <Badge tone="green" testid={`case-status-${c.id.slice(0, 8)}`}>
                <CheckCircle2 size={12} /> Handled
              </Badge>
            ) : (
              <Badge tone="gray" testid={`case-status-${c.id.slice(0, 8)}`}>
                <CircleDot size={12} /> {c.status}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {c.payment_status === "paid" && !c.archived && (
              <button
                data-testid={`case-refund-${c.id.slice(0, 8)}`}
                disabled={updating === c.id}
                onClick={() => refund(c.id)}
                className="flex items-center gap-1.5 text-xs font-bold text-red-600 hover:text-red-800 underline underline-offset-2 disabled:opacity-50"
              >
                <RotateCcw size={12} /> Refund
              </button>
            )}
            {!c.archived &&
              (c.status === "handled" ? (
                <button
                  data-testid={`case-reopen-${c.id.slice(0, 8)}`}
                  disabled={updating === c.id}
                  onClick={() => setStatus(c.id, "received")}
                  className="text-xs font-bold text-[#71717a] hover:text-[#111827] underline underline-offset-2 disabled:opacity-50"
                >
                  Reopen
                </button>
              ) : (
                <button
                  data-testid={`case-handle-${c.id.slice(0, 8)}`}
                  disabled={updating === c.id}
                  onClick={() => setStatus(c.id, "handled")}
                  className="text-xs font-bold bg-[#111827] text-[#FFFFFF] px-4 py-2 rounded-full hover:bg-[#1F2937] transition-colors disabled:opacity-50"
                >
                  {updating === c.id ? "Updating..." : "Mark handled"}
                </button>
              ))}
            <button
              data-testid={`case-archive-${c.id.slice(0, 8)}`}
              disabled={updating === c.id}
              onClick={() => setArchived(c.id, !c.archived)}
              className="flex items-center gap-1.5 text-xs font-bold text-[#71717a] hover:text-[#111827] underline underline-offset-2 disabled:opacity-50"
            >
              {c.archived ? (
                <>
                  <ArchiveRestore size={12} /> Restore
                </>
              ) : (
                <>
                  <Archive size={12} /> Archive
                </>
              )}
            </button>
            <button
              data-testid={`case-delete-${c.id.slice(0, 8)}`}
              disabled={updating === c.id}
              onClick={() => deleteCase(c.id, c.name)}
              className="flex items-center gap-1.5 text-xs font-bold text-red-500 hover:text-red-700 underline underline-offset-2 disabled:opacity-50"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <main data-testid="admin-dashboard" className="pt-[68px] min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <span className="h-[3px] w-12 bg-[#2563EB]" />
              <span className="text-xs font-bold tracking-[0.3em] uppercase text-[#71717a]">
                Admin
              </span>
            </div>
            <h1 className="font-display font-semibold text-4xl tracking-tight">
              Case Dashboard
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="admin-export-button"
              onClick={exportCsv}
              className="flex items-center gap-2 text-sm font-semibold border border-[#E5E7EB] bg-white px-4 py-2.5 rounded-full hover:border-[#111827] transition-colors"
            >
              <Download size={15} /> Export CSV
            </button>
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="flex items-center gap-2 text-sm font-semibold border border-[#E5E7EB] bg-white px-4 py-2.5 rounded-full hover:border-[#111827] transition-colors"
            >
              <RefreshCw size={15} /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-sm font-semibold bg-[#111827] text-[#FFFFFF] px-4 py-2.5 rounded-full hover:bg-[#1F2937] transition-colors"
            >
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Active cases", value: stats?.total_cases ?? "—", testid: "stat-total" },
            {
              label: "Collected",
              value: stats ? `$${stats.revenue_collected.toFixed(2)}` : "—",
              testid: "stat-revenue",
            },
            { label: "Paid", value: stats?.paid_cases ?? "—", testid: "stat-paid" },
            { label: "Handled", value: stats?.handled_cases ?? "—", testid: "stat-handled" },
          ].map((s) => (
            <div
              key={s.label}
              data-testid={s.testid}
              className={`rounded-2xl px-5 py-4 border ${
                s.testid === "stat-revenue"
                  ? "bg-[#111827] border-[#111827]"
                  : "bg-white border-[#E5E7EB]"
              }`}
            >
              <p
                className={`font-display font-semibold text-3xl ${
                  s.testid === "stat-revenue" ? "text-[#60A5FA]" : ""
                }`}
              >
                {s.value}
              </p>
              <p
                className={`text-xs font-semibold mt-1 ${
                  s.testid === "stat-revenue" ? "text-[#a1a1aa]" : "text-[#71717a]"
                }`}
              >
                {s.label}
              </p>
            </div>
          ))}
        </div>

        <div data-testid="filter-chips" className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-5 py-2 rounded-full text-sm font-bold border transition-colors ${
                filter === f.id
                  ? "bg-[#111827] text-[#93C5FD] border-[#111827]"
                  : "bg-white text-[#71717a] border-[#E5E7EB] hover:border-[#111827]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {!cases ? (
          <p className="text-[#71717a]">Loading cases...</p>
        ) : activeCases.length === 0 && archivedCases.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-3xl p-14 text-center">
            <p className="font-display font-semibold text-2xl mb-2">
              {filter === "all" ? "No cases yet" : `No ${filter} cases`}
            </p>
            <p className="text-[#71717a]">
              {filter === "all"
                ? "New submissions from the Start Your Case form will appear here."
                : "Try a different filter."}
            </p>
          </div>
        ) : (
          <>
            {activeCases.length === 0 ? (
              <p className="text-[#71717a] py-6">No {filter} cases right now.</p>
            ) : (
              <div className="space-y-4">{activeCases.map(renderCase)}</div>
            )}
            {archivedCases.length > 0 && (
              <div className="mt-8">
                <button
                  data-testid="toggle-archived-button"
                  onClick={() => setShowArchived((s) => !s)}
                  className="flex items-center gap-2 text-sm font-bold text-[#71717a] hover:text-[#111827] transition-colors"
                >
                  <Archive size={15} />
                  {showArchived ? "Hide" : "Show"} archived ({archivedCases.length})
                </button>
                {showArchived && (
                  <div className="space-y-4 mt-4">{archivedCases.map(renderCase)}</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
